import React, { useState, useEffect, useCallback } from 'react';
import { 
    XMarkIcon, 
    MagnifyingGlassIcon, 
    UserPlusIcon, 
    DocumentArrowUpIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';

const ClubEnrollmentModal = ({ clubId, clubName, onClose, onRefresh }) => {
    const { get, post } = useApi();
    const { showToast } = useAppState();
    const [activeTab, setActiveTab] = useState('manual');
    const [searchTerm, setSearchTerm] = useState('');
    const [availableStudents, setAvailableStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [grades, setGrades] = useState([]);
    const [selectedGrade, setSelectedGrade] = useState('');
    const [enrollingId, setEnrollingId] = useState(null);
    const [successId, setSuccessId] = useState(null);

    const fileInputRef = React.useRef(null);

    const fetchGrades = useCallback(async () => {
        try {
            const data = await get('/api/cbc/grade-levels/');
            setGrades(data || []);
        } catch (err) {
            console.error('Failed to fetch grades');
        }
    }, [get]);

    useEffect(() => {
        fetchGrades();
    }, [fetchGrades]);

    const handleSearch = useCallback(async () => {
        if (!searchTerm && !selectedGrade) return;
        setLoading(true);
        try {
            const query = `name=${searchTerm}&grade_level=${selectedGrade}`;
            const response = await get(`/teachers/api/students/search/?${query}`);
            setAvailableStudents(response || []);
        } catch (err) {
            showToast('Failed to search students', 'error');
        } finally {
            setLoading(false);
        }
    }, [get, searchTerm, selectedGrade, showToast]);

    // Debounce search
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (searchTerm || selectedGrade) handleSearch();
        }, 500);
        return () => clearTimeout(timeout);
    }, [searchTerm, selectedGrade, handleSearch]);

    const handleEnroll = async (studentId) => {
        setEnrollingId(studentId);
        try {
            await post(`/api/events/clubs/${clubId}/enroll-students/`, { student_ids: [studentId] });
            setSuccessId(studentId);
            showToast('Student enrolled successfully', 'success');
            
            // Wait for animation
            setTimeout(() => {
                setAvailableStudents(prev => prev.filter(s => s.id !== studentId));
                setSuccessId(null);
                setEnrollingId(null);
                onRefresh && onRefresh();
            }, 1000);
        } catch (err) {
            showToast('Enrollment failed', 'error');
            setEnrollingId(null);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setImportLoading(true);
        setImportResult(null);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await post(`/api/events/clubs/${clubId}/upload-roster/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setImportResult(response);
            showToast(response.message || 'Import completed', 'success');
            onRefresh && onRefresh();
        } catch (err) {
            showToast('Import failed. Please check the file format.', 'error');
        } finally {
            setImportLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 sm:p-6">
            <div 
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" 
                onClick={onClose}
            ></div>
            
            <div className="relative bg-white w-full max-w-4xl rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in duration-300 flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="px-8 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                    <div>
                        <h2 className="text-2xl font-black text-[#18216D] uppercase tracking-tight italic">Enroll Scholars</h2>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">{clubName} • Patron Protocol</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-rose-500 hover:border-rose-100 transition-all shadow-sm"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex bg-slate-100/50 p-1.5 mx-8 mt-3 rounded-2xl">
                    <button 
                        onClick={() => setActiveTab('manual')}
                        className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === 'manual' ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        Manual Search
                    </button>
                    <button 
                        onClick={() => setActiveTab('import')}
                        className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === 'import' ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        Excel Import
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                    {activeTab === 'manual' ? (
                        <div className="space-y-6">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="relative flex-1 group">
                                    <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D]" />
                                    <input 
                                        type="text"
                                        placeholder="Identification Lookup..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-12 pr-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-bold text-[#18216D] focus:ring-2 focus:ring-indigo-500/10 transition-all"
                                    />
                                </div>
                                <select 
                                    value={selectedGrade}
                                    onChange={(e) => setSelectedGrade(e.target.value)}
                                    className="px-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-bold text-[#18216D] focus:ring-2 focus:ring-indigo-500/10 appearance-none min-w-[140px]"
                                >
                                    <option value="">All Grades</option>
                                    {grades.map(g => (
                                        <option key={g.id} value={g.id}>{g.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="space-y-2">
                                {loading ? (
                                    <div className="space-y-4">
                                        {[1, 2, 3, 4].map(i => (
                                            <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl animate-pulse">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 bg-slate-100 rounded-xl"></div>
                                                    <div className="space-y-2">
                                                        <div className="h-3 w-32 bg-slate-100 rounded"></div>
                                                        <div className="h-2 w-20 bg-slate-100 rounded"></div>
                                                    </div>
                                                </div>
                                                <div className="h-8 w-20 bg-slate-100 rounded-lg"></div>
                                            </div>
                                        ))}
                                    </div>
                                ) : availableStudents.length > 0 ? (
                                    availableStudents.map(student => (
                                        <div key={student.id} className="flex items-center justify-between p-4 bg-slate-50 hover:bg-white hover:shadow-xl hover:shadow-indigo-900/5 rounded-2xl border border-transparent hover:border-indigo-100 transition-all group">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 bg-indigo-50 text-[#18216D] rounded-xl flex items-center justify-center font-black text-xs border border-indigo-100">
                                                    {student.name?.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-black text-[#18216D]">{student.name}</p>
                                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{student.grade_level_name} • {student.student_id}</p>
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => handleEnroll(student.id)}
                                                disabled={enrollingId === student.id}
                                                className={`px-4 py-2 text-[9px] font-black rounded-lg transition-all uppercase tracking-widest flex items-center gap-2 group-hover:scale-105 ${
                                                    successId === student.id 
                                                    ? 'bg-emerald-500 text-white' 
                                                    : 'bg-[#18216D] text-white hover:bg-[#0D164F]'
                                                } disabled:opacity-50`}
                                            >
                                                {successId === student.id ? (
                                                    <CheckCircleIcon className="w-4 h-4 animate-bounce" />
                                                ) : enrollingId === student.id ? (
                                                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                                ) : (
                                                    <UserPlusIcon className="w-4 h-4" />
                                                )}
                                                {successId === student.id ? 'Enrolled' : enrollingId === student.id ? 'Linking...' : 'Enroll'}
                                            </button>
                                        </div>
                                    ))
                                ) : searchTerm || selectedGrade ? (
                                    <div className="py-12 text-center text-slate-300 font-black uppercase tracking-widest italic">No matches found in database</div>
                                ) : (
                                    <div className="py-12 text-center">
                                        <UserPlusIcon className="w-12 h-12 text-slate-100 mx-auto mb-4" />
                                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">Enter details to begin lookup</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            <div className="text-center p-12 bg-slate-50 rounded-[2.5rem] border-2 border-dashed border-slate-200 group hover:border-indigo-300 transition-all">
                                <input 
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    accept=".xlsx,.xls,.csv"
                                />
                                <DocumentArrowUpIcon className="w-16 h-16 text-slate-300 mx-auto mb-6 group-hover:text-indigo-400 transition-colors" />
                                <h3 className="text-lg font-black text-[#18216D] uppercase tracking-tight italic mb-2">Bulk Enrollment Phase</h3>
                                <p className="text-xs text-slate-400 font-medium mb-8 max-w-sm mx-auto leading-relaxed">
                                    Upload a standardized Excel template. Mandatory columns: <span className="font-bold text-[#18216D]">ID</span> and <span className="font-bold text-[#18216D]">Student Name</span>.
                                </p>
                                <button 
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={importLoading}
                                    className="px-8 py-4 bg-[#FFC425] text-[#18216D] text-[10px] font-black rounded-2xl hover:bg-yellow-400 transition-all uppercase tracking-widest shadow-xl shadow-amber-500/10 flex items-center gap-3 mx-auto disabled:opacity-50"
                                >
                                    {importLoading ? 'Processing Registry...' : 'Select Excel Document'}
                                </button>
                            </div>

                            {importResult && (
                                <div className="p-6 bg-emerald-50 rounded-[2rem] border border-emerald-100 animate-in slide-in-from-bottom-4 duration-500">
                                    <div className="flex items-center gap-3 mb-4">
                                        <CheckCircleIcon className="w-6 h-6 text-emerald-500" />
                                        <h4 className="text-sm font-black text-emerald-900 uppercase tracking-tight">Import Synchronization Complete</h4>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-white/50 p-4 rounded-[1.5rem]">
                                            <p className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">Enrolled</p>
                                            <p className="text-2xl font-black text-emerald-900">{importResult.enrolled_count}</p>
                                        </div>
                                        <div className="bg-white/50 p-4 rounded-[1.5rem]">
                                            <p className="text-[9px] font-black text-rose-600 uppercase tracking-widest">Anomalies</p>
                                            <p className="text-2xl font-black text-rose-900">{importResult.errors?.length || 0}</p>
                                        </div>
                                    </div>
                                    {importResult.errors?.length > 0 && (
                                        <div className="mt-4 max-h-32 overflow-y-auto custom-scrollbar pr-2">
                                            {importResult.errors.map((error, idx) => (
                                                <p key={idx} className="text-[9px] text-rose-500 font-bold mb-1">• {error}</p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-8 bg-slate-50 border-t border-slate-100 flex justify-end">
                    <button 
                        onClick={onClose}
                        className="px-8 py-3 bg-white border border-slate-200 text-slate-400 rounded-xl font-black text-[10px] uppercase tracking-widest hover:text-rose-500 hover:border-rose-100 transition-all"
                    >
                        Close Portal
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ClubEnrollmentModal;
